import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

import { QuoteProps, QuoteLineItemProps } from '@partsbadger/types';
import { MaterialProps, FinishesProps, CoatingsProps } from '@partsbadger/types';
import _ from 'lodash';

import PartRow from './PartRow';
import { json } from 'stream/consumers';

interface PartsManagerProps {
    activePart: number | null;
    setActivePart: (partId: number | null) => void;
    onUpdateLineItem: <T>(data: T) => void;
    materialsList: MaterialProps[];
    topMaterials: MaterialProps[];
    coatingsList: CoatingsProps[];
    topCoatings: CoatingsProps[];
    finishesList: FinishesProps[];
    loadingPartId: number | null;
    onUpdateItemPrice: (itemId: number, leadTimeOptionId: string) => void;
    onRemoveMasterProduct: (masterProductId: number) => void;
    onDuplicateItem: (masterProductId: number) => void;
    onRemoveVariation: (uid: string) => void;
    errorPartId: number | null;
    errorStepId: number | null;
    scrollTick: number;
    quoteToken: string;
    isRapidQuote: boolean;
    sortedMasterProducts: QuoteLineItemProps[][];
    isHobbyist: boolean;
    isRequote: boolean;
    lineItemsLength: number;
    onPendingCustomChange?: (partId: number, stepIndex: number, isPending: boolean) => void;
}

const PartsContainer: React.FC<PartsManagerProps> = ({
    activePart,
    setActivePart,
    onUpdateLineItem,
    materialsList,
    topMaterials,
    coatingsList,
    topCoatings,
    finishesList,
    loadingPartId,
    onUpdateItemPrice,
    onRemoveMasterProduct,
    onDuplicateItem,
    onRemoveVariation,
    errorPartId,
    errorStepId,
    scrollTick,
    quoteToken,
    isRapidQuote,
    sortedMasterProducts,
    isHobbyist,
    isRequote,
    lineItemsLength,
    onPendingCustomChange,
}) => {
    const [currentSteps, setCurrentSteps] = useState<Record<number, number>>({});

    const partRefs = useRef<Record<number, HTMLDivElement | null>>({});

    const setStep = useCallback((partId: number, step: number) => {
        setCurrentSteps(prev => {
            if (prev[partId] === step) return prev; // bail out
            return { ...prev, [partId]: step };
        });
    }, []);

    useEffect(() => {
        if (errorPartId == null) return;

        requestAnimationFrame(() => {
            partRefs.current[errorPartId]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        });

        if (errorStepId) {
            setStep(errorPartId, errorStepId);
        }
    }, [errorPartId, errorStepId, scrollTick]);

    const togglePartExpansion = useCallback(
        (partId: number) => {
            setActivePart(activePart === partId ? null : partId);
        },
        [activePart, setActivePart]
    );

    const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (submitTimeoutRef.current) {
                clearTimeout(submitTimeoutRef.current);
            }
        };
    }, []);

    // const master_products = useMemo(() => {
    //     return _.groupBy(quote?.line_items, 'product.master_product');
    // }, [quote?.line_items, quote?.line_items?.length]);

    // const sortedMasterProducts = useMemo(() => {
    //     return Object.values(master_products).map(group => {
    //         // Only sort items within group if there are variations (more than 1 item)
    //         if (group.length > 1) {
    //             return [...group].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    //         }
    //         // Single item groups maintain their original order
    //         return group;
    //     });
    // }, [master_products]);

    const isCustomValueMissing = (selected?: string, customValue?: string | null): boolean => {
        return selected === 'Custom' && !customValue?.trim();
    };

    return (
        <>
            {/* Show PartsManager only when parts exist */}
            {lineItemsLength > 0 && (
                <div className={'parts-container'}>
                    <div className=" rounded-2xl  overflow-hidden mb- py-2">
                        {sortedMasterProducts?.map((group, groupIndex) => (
                            <React.Fragment key={group[0]?.product?.master_product}>
                                {group?.map((part, index) => {
                                    const isVariation = index >= 1;
                                    const isFirstPart = groupIndex === 0 && index === 0;

                                    const isExpanded = part?.id === activePart;

                                    const getCurrentStep = currentSteps[part.id] ?? 0;

                                    const isActive = part.id === activePart;
                                    const showError = errorPartId === part.id;
                                    const masterName = group[0]?.name ?? '';
                                    const variantName = isVariation ? `Variant #${index} of ${masterName}` : null;

                                    // Todo: Backend should provide a way to check if 3D file analysis failed for a part as a single flag
                                    const analysisStatus = part.product?.analysis_status;
                                    const has3DFileAnalysisError =
                                        analysisStatus?.file_3d?.completed === true &&
                                        analysisStatus?.file_3d?.status === 'FAILURE';

                                    const hasCustomMaterialError =
                                        !part.product?.material ||
                                        isCustomValueMissing(
                                            part.product.material,
                                            part.product.custom_material ?? null
                                        );

                                    const hasCustomFinishError =
                                        !part.product.finish_related ||
                                        isCustomValueMissing(
                                            part.product.finish_related.name,
                                            part.product.custom_finish ?? null
                                        );

                                    const hasCustomCoatingError =
                                        !part.product.coating_related ||
                                        isCustomValueMissing(
                                            part.product.coating_related.name,
                                            part.product.custom_coating ?? null
                                        );

                                    return (
                                        <PartRow
                                            key={part.id}
                                            part={part}
                                            isFirstPart={isFirstPart}
                                            /* state */
                                            isExpanded={isExpanded}
                                            isVariation={isVariation}
                                            isActive={isActive}
                                            showError={
                                                showError ||
                                                has3DFileAnalysisError ||
                                                hasCustomMaterialError ||
                                                hasCustomFinishError ||
                                                hasCustomCoatingError
                                            }
                                            /* step handling */
                                            currentStep={getCurrentStep}
                                            setStep={setStep}
                                            /* flags */
                                            loadingPartId={loadingPartId}
                                            isRapidQuote={isRapidQuote}
                                            isRequote={isRequote}
                                            quoteToken={quoteToken}
                                            isHobbyist={isHobbyist}
                                            /* data */
                                            materialsList={materialsList}
                                            topMaterials={topMaterials}
                                            finishesList={finishesList}
                                            // coatingsList={coatingsList.get(part?.product?.material_related?.id) ?? []}
                                            coatingsList={coatingsList}
                                            topCoatings={topCoatings}
                                            /* actions */
                                            togglePartExpansion={togglePartExpansion}
                                            handleSubmit={onUpdateLineItem}
                                            onUpdateItemPrice={onUpdateItemPrice}
                                            onDuplicateItem={onDuplicateItem}
                                            onRemoveMasterProduct={onRemoveMasterProduct}
                                            onRemoveVariation={onRemoveVariation}
                                            onPendingCustomChange={onPendingCustomChange}
                                            /* refs */
                                            partRefs={partRefs}
                                            variantName={variantName}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

// Signature for option lists so we re-render when materials/coatings/finishes update
const createListSignature = (arr: { id?: number }[]): string => `${arr.length}:${arr.map(x => x.id ?? '').join(',')}`;

// const createCoatingsMapSignature = (map: Map<number, CoatingsProps[]>): string =>
//     `${map.size}:${[...map.entries()]
//         .map(([k, v]) => `${k}:${v.length}`)
//         .sort()
//         .join(',')}`;

// Helper function to create a signature of sortedMasterProducts including analysis_status, thumbnail, and prices
// so that WebSocket updates (e.g. thumbnail, analysis status) cause re-renders and are not cached by React.memo.
const createSortedProductsSignature = (sortedMasterProducts: QuoteLineItemProps[][]): string => {
    return sortedMasterProducts
        .map(group =>
            group
                .map(item => {
                    const file2dCompleted = item?.product?.analysis_status?.file_2d?.completed ?? false;
                    const file3dCompleted = item?.product?.analysis_status?.file_3d?.completed ?? false;
                    const file2dStatus = item?.product?.analysis_status?.file_2d?.status ?? '';
                    const file3dStatus = item?.product?.analysis_status?.file_3d?.status ?? '';
                    const hasFile2d = item?.product?.has_file_2d ?? false;
                    const hasFile3d = item?.product?.has_file_3d ?? false;

                    const unitPrice = item?.unit_price ?? '0';
                    const totalPrice = item?.total_price ?? '0';
                    const totalAfterDiscount = item?.total_after_discount ?? 0;
                    const quantity = item?.quantity ?? 0;
                    const leadTime = item?.lead_time ?? 0;

                    const isFastlaneSafe = item?.is_fastlane_safe ?? false;
                    const isStandardSafe = item?.is_standard_safe ?? false;
                    const safeAutoQuote = item?.safe_auto_quote ?? false;
                    const needsManualQuote = item?.needs_manual_quote ?? false;
                    const pricingInProgress = item?.is_pricing_in_progress ?? false;
                    const quoteAction = item?.quote_action ?? '';

                    const pricingPartId = item?.pricing_options?.part_id ?? 'none';
                    const masterProductEditable = item?.master_product_details?.editable;
                    const masterProductDetailsId = item?.master_product_details?.id ?? '';
                    const productMasterProductId = item?.product?.master_product ?? '';
                    const pricingFeedback = (item?.customer_pricing_feedback ?? []).join('~');
                    const manufacturingType = item?.manufacturing_type ?? '';

                    return [
                        item.id,
                        productMasterProductId,
                        masterProductDetailsId,
                        masterProductEditable === undefined ? '' : masterProductEditable ? '1' : '0',
                        hasFile2d ? '1' : '0',
                        hasFile3d ? '1' : '0',
                        file2dCompleted ? '1' : '0',
                        file3dCompleted ? '1' : '0',
                        file2dStatus,
                        file3dStatus,
                        quantity,
                        leadTime,
                        unitPrice,
                        totalPrice,
                        totalAfterDiscount,
                        isFastlaneSafe ? '1' : '0',
                        isStandardSafe ? '1' : '0',
                        safeAutoQuote ? '1' : '0',
                        needsManualQuote ? '1' : '0',
                        pricingInProgress ? '1' : '0',
                        quoteAction,
                        pricingPartId,
                        pricingFeedback,
                        manufacturingType,
                    ].join(':');
                })
                .join(',')
        )
        .join('|');
};

// Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: PartsManagerProps, nextProps: PartsManagerProps) => {
    // Quick checks for primitive values
    if (
        prevProps.activePart !== nextProps.activePart ||
        prevProps.loadingPartId !== nextProps.loadingPartId ||
        prevProps.errorPartId !== nextProps.errorPartId ||
        prevProps.errorStepId !== nextProps.errorStepId ||
        prevProps.scrollTick !== nextProps.scrollTick ||
        prevProps.quoteToken !== nextProps.quoteToken ||
        prevProps.isRapidQuote !== nextProps.isRapidQuote ||
        prevProps.isHobbyist !== nextProps.isHobbyist ||
        prevProps.isRequote !== nextProps.isRequote ||
        prevProps.lineItemsLength !== nextProps.lineItemsLength
    ) {
        return false;
    }

    // Check if sortedMasterProducts structure or content changed
    // This includes checking analysis_status changes
    const prevSignature = createSortedProductsSignature(prevProps.sortedMasterProducts);
    const nextSignature = createSortedProductsSignature(nextProps.sortedMasterProducts);

    if (prevSignature !== nextSignature) {
        return false;
    }

    // Re-render when option lists (materials, coatings, finishes) change
    if (createListSignature(prevProps.materialsList) !== createListSignature(nextProps.materialsList)) {
        return false;
    }
    if (createListSignature(prevProps.topMaterials) !== createListSignature(nextProps.topMaterials)) {
        return false;
    }
    if (createListSignature(prevProps.topCoatings) !== createListSignature(nextProps.topCoatings)) {
        return false;
    }
    if (createListSignature(prevProps.finishesList) !== createListSignature(nextProps.finishesList)) {
        return false;
    }
    if (createListSignature(prevProps.coatingsList) !== createListSignature(nextProps.coatingsList)) {
        return false;
    }

    // Callbacks should be stable (memoized in parent), so we don't need to check them
    // If they're not stable, re-render is expected

    return true;
};

export default React.memo(PartsContainer, arePropsEqual);
